<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import FunnelFieldInput from 'dashboard/components-next/funnels/FunnelFieldInput.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedFunnelId = ref('');
const selectedStageId = ref('');
const isCreating = ref(false);
// Values typed into the selected funnel's required fields, keyed by field key.
const fieldValues = ref({});
// Map of field key -> field name rejected by the structured 422 (§9.6).
const fieldErrors = ref({});

const funnels = useMapGetter('funnels/getFunnels');

const funnelOptions = computed(() =>
  funnels.value.map(funnel => ({ value: funnel.id, label: funnel.name }))
);

const selectedFunnel = computed(() =>
  funnels.value.find(funnel => funnel.id === selectedFunnelId.value)
);

// Required fields must be filled at creation time; optional ones can be filled
// later from the board card, keeping this dialog focused.
const requiredFields = computed(() =>
  [...(selectedFunnel.value?.fields || [])]
    .filter(field => field.required)
    .sort((a, b) => a.position - b.position)
);

const errorFor = field =>
  fieldErrors.value[field.key]
    ? t('FUNNELS.CARD.EDIT.REQUIRED_ERROR', { name: field.name })
    : '';

const setFieldValue = (field, value) => {
  fieldValues.value = { ...fieldValues.value, [field.key]: value };
};

const stageOptions = computed(() =>
  [...(selectedFunnel.value?.stages || [])]
    .sort((a, b) => a.position - b.position)
    .map(stage => ({ value: stage.id, label: stage.name }))
);

const isConfirmDisabled = computed(
  () => !selectedFunnelId.value || !selectedStageId.value
);

// Reset the stage and field values whenever the funnel changes; both only make
// sense within the selected funnel.
watch(selectedFunnelId, () => {
  selectedStageId.value = stageOptions.value[0]?.value || '';
  fieldValues.value = {};
  fieldErrors.value = {};
});

const open = async () => {
  if (!funnels.value.length) {
    await store.dispatch('funnels/fetchFunnels');
  }
  selectedFunnelId.value = funnelOptions.value[0]?.value || '';
  dialogRef.value?.open();
};

const handleConfirm = async () => {
  if (isConfirmDisabled.value) return;
  isCreating.value = true;
  fieldErrors.value = {};
  try {
    await store.dispatch('funnels/createCard', {
      funnelId: selectedFunnelId.value,
      funnelStageId: selectedStageId.value,
      conversationId: props.conversationId,
      customFieldValues: fieldValues.value,
    });
    useAlert(t('FUNNELS.ADD_TO_FUNNEL.SUCCESS'));
    dialogRef.value?.close();
  } catch (error) {
    const errors = error?.response?.data?.errors;
    if (error?.response?.status === 422 && Array.isArray(errors)) {
      // Structured 422: a required field is missing — flag it inline instead of
      // a toast so the user knows exactly what to fill.
      fieldErrors.value = errors.reduce(
        (acc, item) => ({ ...acc, [item.key]: item.name }),
        {}
      );
    } else {
      // The (funnel_id, conversation_id) uniqueness index surfaces as a plain
      // 422 when the conversation is already on this funnel.
      const message =
        error?.response?.status === 422
          ? t('FUNNELS.ADD_TO_FUNNEL.ALREADY_EXISTS')
          : t('FUNNELS.ADD_TO_FUNNEL.ERROR');
      useAlert(message);
    }
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('FUNNELS.ADD_TO_FUNNEL.TITLE')"
    :confirm-button-label="t('FUNNELS.ADD_TO_FUNNEL.CONFIRM')"
    :disable-confirm-button="isConfirmDisabled"
    :is-loading="isCreating"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('FUNNELS.ADD_TO_FUNNEL.FUNNEL_LABEL') }}
        </label>
        <Select
          v-model="selectedFunnelId"
          class="w-full"
          :options="funnelOptions"
          :placeholder="t('FUNNELS.ADD_TO_FUNNEL.FUNNEL_PLACEHOLDER')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('FUNNELS.ADD_TO_FUNNEL.STAGE_LABEL') }}
        </label>
        <Select
          v-model="selectedStageId"
          class="w-full"
          :options="stageOptions"
          :placeholder="t('FUNNELS.ADD_TO_FUNNEL.STAGE_PLACEHOLDER')"
        />
      </div>

      <FunnelFieldInput
        v-for="field in requiredFields"
        :key="field.id"
        :field="field"
        :model-value="fieldValues[field.key] ?? ''"
        :error="errorFor(field)"
        @update:model-value="value => setFieldValue(field, value)"
      />
    </div>
  </Dialog>
</template>
